// components/CountdownButton.tsx
import { Button } from '@douyinfe/semi-ui'
import type { ComponentProps } from 'react'
type ButtonProps = ComponentProps<typeof Button>
import React from 'react'
import { useState, useEffect, useRef } from 'react'

export interface CountdownButtonProps extends Omit<ButtonProps, 'onClick'> {
	/** 倒计时总秒数，默认 60 */
	duration?: number
	/** 点击时的回调函数（必须返回 Promise 或 void） */
	onClick: (startCountdown: () => void) => void | Promise<void>
	/** 倒计时期间显示的文本模板，{seconds} 会被替换 */
	countdownText?: string // 默认: '{seconds} 秒后重试'
	/** 初始按钮文本 */
	defaultButtonText?: string // 默认: '获取验证码'
}

export const CountdownButton = ({
	duration = 60,
	onClick,
	countdownText = '{seconds} 秒后重试',
	defaultButtonText = '获取验证码',
	disabled: disabledProp,
	...buttonProps
}: CountdownButtonProps) => {
	const [countdown, setCountdown] = useState(0)
	const timerRef = useRef<NodeJS.Timeout | null>(null)

	// 清理定时器（防内存泄漏）
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
			}
		}
	}, [])

	const startCountdown = () => {
		setCountdown(duration)
	}

	const handleClick = async () => {
		// 如果正在倒计时，直接忽略点击
		if (countdown > 0) return

		// 调用用户传入的 onClick，并传入 startCountdown 控制权
		await onClick(startCountdown)
	}

	// 倒计时逻辑
	useEffect(() => {
		if (countdown > 0) {
			timerRef.current = setTimeout(() => {
				setCountdown((prev) => prev - 1)
			}, 1000)
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
			}
		}
	}, [countdown, duration])

	const isCounting = countdown > 0
	const displayText = isCounting ? countdownText.replace('{seconds}', countdown.toString()) : defaultButtonText

	return (
		<Button {...buttonProps} disabled={isCounting || disabledProp} onClick={handleClick}>
			{displayText}
		</Button>
	)
}
